import React from 'react';
import { Table, TableProps } from 'antd';
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface DragSortingTableProps extends TableProps<any> {
    onMove: (data: Array<any>) => void;
    indexColumn?: string;
    rowKey: string; // dnd-kit requires a unique rowKey (usually 'id' or 'key')
}

// 1. The Draggable Row Component
const DraggableBodyRow = ({ children, ...props }: any) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: props['data-row-key'],
    });

    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 })?.replace(
            /translate3d\(([^,]+),([^,]+),([^)]+)\)/,
            'translate3d(0, $2, 0)' // Vertical only movement
        ),
        transition,
        cursor: 'move',
        ...(isDragging ? { position: 'relative', zIndex: 9999, background: '#fafafa' } : {}),
    };

    return (
        <tr
            {...props}
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
        >
            {children}
        </tr>
    );
};

// 2. The Main Table Component
const DragSortingTable = (props: DragSortingTableProps) => {
    const { dataSource, onMove, indexColumn, rowKey } = props;

    // Set up Sensors with a distance constraint to keep buttons clickable
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // 5px movement required to start dragging
            },
        })
    );

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id && over) {
            const data = [...(dataSource || [])];

            // Find indexes based on the rowKey
            const oldIndex = data.findIndex((item) => item[rowKey] === active.id);
            const newIndex = data.findIndex((item) => item[rowKey] === over.id);

            // Reorder the array
            let sortedData = arrayMove(data, oldIndex, newIndex);

            // Handle the optional indexColumn logic from your original code
            if (indexColumn) {
                sortedData.forEach((item, index) => {
                    sortedData[index][indexColumn] = sortedData.length - index;
                });
            }

            onMove(sortedData);
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
        >
            <SortableContext
                items={dataSource?.map((i) => i[rowKey]) || []}
                strategy={verticalListStrategy}
            >
                <Table
                    {...props}
                    components={{
                        body: {
                            row: dataSource?.length ? DraggableBodyRow : undefined,
                        },
                    }}
                />
            </SortableContext>
        </DndContext>
    );
};

// Helper for the strategy (dnd-kit export names can vary slightly by version)
const verticalListStrategy = verticalListSortingStrategy;

export default DragSortingTable;